/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import ObjectModel from 'cio/models/object';
import CustomerModel from 'cio/models/customer';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dropTask, restartableTask } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import ObjectTypeModel from 'cio/models/object-type';
import ObjectTypesService from 'cio/services/object-types';
import ApiService from 'cio/services/api';
import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import FeaturesService from 'ember-feature-flags/services/features';
import AttributesProxy from 'cio/app-models/customer/attributes-proxy';

interface Args {
  objectType?: ObjectTypeModel;
  onClose: () => unknown;
  object?: ObjectModel;
  customer?: CustomerModel;
  onRelationshipsAdd?: () => unknown;
  source: string;
}

enum ModalSteps {
  People = 'people',
  Objects = 'objects',
  Attributes = 'attributes',
  Review = 'review',
}

export default class AddRelationshipsModal extends Component<Args> {
  @service store!: DS.Store;
  @service objectTypes!: ObjectTypesService;
  @service api!: ApiService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service features!: FeaturesService;

  @tracked step = this.args.customer ? ModalSteps.Objects : ModalSteps.People;
  @tracked selectedPeople: CustomerModel[] = this.args.customer ? [this.args.customer] : [];
  @tracked totalPeopleRecs = -1;
  @tracked peoplePerPage = 50;

  @tracked selectedObjects: ObjectModel[] = this.args.object ? [this.args.object] : [];
  @tracked totalObjectsRecs = -1;
  @tracked objectsPerPage = 50;

  @tracked peopleFilters = { email: '', filters: '', page: 1 };
  @tracked objectFilters = { filters: '', page: 1, name: '' };

  modalSteps = ModalSteps;
  attributesProxy = AttributesProxy.create({
    attrs: [],
    requiredAttributes: [],
    autoGeneratedAttributes: [],
  });
  readonly isAttributesEnabled = this.features.isEnabled('campaign_trigger_ui_new');

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.loadPeopleTask.perform();
    this.loadObjectsTask.perform();
    this.store.findAll('short-segment');

    this.analytics.track('objects', 'relationships_manual_add_started', { source: args.source });
  }

  get objectType() {
    return this.args.objectType ?? this.objectTypes.sortedObjectTypes.firstObject;
  }

  get modalNavSteps() {
    return [
      ...(this.args.customer == null
        ? [
            {
              disabled: this.isLoading,
              validationKey: this.selectedPeople.length > 0,
              title: 'Select People',
              onClick: () => (this.step = ModalSteps.People),
              isActive: this.step === ModalSteps.People,
            },
          ]
        : []),
      ...(this.args.object == null
        ? [
            {
              disabled: this.isLoading || (this.args.customer == null && this.selectedPeople.length === 0),
              validationKey: this.selectedObjects.length > 0,
              disableMessage: 'You must select people before moving on to the next steps!',
              title: `Select ${this.args.objectType != null ? this.args.objectType.name : 'Objects'}`,
              onClick: () => (this.step = ModalSteps.Objects),
              isActive: this.step === ModalSteps.Objects,
            },
          ]
        : []),
      ...(this.isAttributesEnabled
        ? [
            {
              disabled:
                this.isLoading ||
                (this.args.customer == null && this.selectedPeople.length === 0) ||
                (this.args.object == null && this.selectedObjects.length === 0),
              validationKey: this.step === ModalSteps.Review,
              disableMessage: `You must select ${
                this.step === ModalSteps.People ? 'people' : 'objects'
              } before moving on to the next steps!`,
              title: 'Add Attributes',
              onClick: () => (this.step = ModalSteps.Attributes),
              isActive: this.step === ModalSteps.Attributes,
            },
          ]
        : []),
      {
        disabled:
          this.isLoading ||
          (this.args.customer == null && this.selectedPeople.length === 0) ||
          (this.args.object == null && this.selectedObjects.length === 0),
        validationKey: false,
        disableMessage: `You must select ${
          this.step === ModalSteps.People ? 'people' : 'objects'
        } before moving on to the next steps!`,
        title: 'Review',
        onClick: () => (this.step = ModalSteps.Review),
        isActive: this.step === ModalSteps.Review,
      },
    ];
  }

  get isLoading() {
    return this.loadPeopleTask.isRunning || this.loadObjectsTask.isRunning || this.addRelationshipsTask.isRunning;
  }

  get isNextButtonDisabled() {
    if (this.step === ModalSteps.People) {
      return this.selectedPeople.length === 0;
    }

    if (this.step === ModalSteps.Objects) {
      return this.selectedObjects.length === 0;
    }

    return false;
  }

  @action
  setSelectedPeople(people: CustomerModel[]) {
    this.selectedPeople = [...people];
  }

  @action
  setSelectedObjects(objects: ObjectModel[]) {
    this.selectedObjects = [...objects];
  }

  @action
  prevStep() {
    switch (this.step) {
      case ModalSteps.People:
        this.args.onClose();
        break;
      case ModalSteps.Objects:
        if (this.args.customer == null) {
          this.step = ModalSteps.People;
        } else {
          this.args.onClose();
        }
        break;
      case ModalSteps.Attributes:
        if (this.args.object == null) {
          this.step = ModalSteps.Objects;
        } else {
          this.step = ModalSteps.People;
        }
        break;
      case ModalSteps.Review:
        if (this.isAttributesEnabled) {
          this.step = ModalSteps.Attributes;
        } else if (this.args.object == null) {
          this.step = ModalSteps.Objects;
        } else {
          this.step = ModalSteps.People;
        }
        break;
    }
  }

  @action
  nextStep() {
    switch (this.step) {
      case ModalSteps.People:
        if (this.args.object == null) {
          this.step = ModalSteps.Objects;
        } else if (this.isAttributesEnabled) {
          this.step = ModalSteps.Attributes;
        } else {
          this.step = ModalSteps.Review;
        }
        break;
      case ModalSteps.Objects:
        if (this.isAttributesEnabled) {
          this.step = ModalSteps.Attributes;
        } else {
          this.step = ModalSteps.Review;
        }
        break;
      case ModalSteps.Attributes:
        this.step = ModalSteps.Review;
        break;
      case ModalSteps.Review:
        this.addRelationshipsTask.perform();
    }
  }

  @action
  setPeopleFilters({
    email = this.peopleFilters.email,
    filters = this.peopleFilters.filters,
    page = this.peopleFilters.page,
  }) {
    this.peopleFilters = {
      email,
      filters,
      page,
    };
  }

  @action
  setObjectFilters({
    filters = this.objectFilters.filters,
    page = this.objectFilters.page,
    name = this.objectFilters.name,
  }) {
    this.objectFilters = {
      filters,
      page,
      name,
    };
  }

  loadPeopleTask = restartableTask(async () => {
    let customers = await this.store.query('customer', {
      ...this.peopleFilters,
      sort: 'created_at',
      asc: false,
    });

    this.totalPeopleRecs = (customers as any).meta?.pagination?.total ?? -1;
    this.peoplePerPage = (customers as any).meta?.pagination?.size ?? 50;

    return customers.toArray();
  });

  loadObjectsTask = restartableTask(async (objectTypeId?: string) => {
    let objects = await this.store.query('object', {
      ...this.objectFilters,
      objectTypeId: objectTypeId ?? this.objectType?.id ?? '1',
    });

    this.totalObjectsRecs = (objects as any).meta?.pagination?.total ?? -1;
    this.objectsPerPage = (objects as any).meta?.pagination?.size ?? 50;

    return objects.toArray();
  });

  addRelationshipsTask = dropTask(async () => {
    let relationships: any[] = [];
    let { attrs } = this.attributesProxy.getModelAttributes();

    this.selectedObjects.forEach((objectType) => {
      this.selectedPeople.forEach((person) => {
        relationships.push({
          entity1_type: 'customer',
          entity1_id: person.id,
          entity2_type: 'object',
          entity2_id: objectType.id,
          attributes: attrs,
        });
      });
    });

    try {
      await this.api.post(`relationships`, { data: { relationships } });
      this.alerts.success('relationships.add', 'Relationships added');

      this.analytics.track('objects', 'relationships_manual_add_completed', {
        source: this.args.source,
        peopleCount: this.selectedPeople.length,
        objectCount: this.selectedObjects.length,
        relationshipAttributesCount: Object.keys(attrs).length,
      });

      let selectedObjectType = this.selectedObjects[0].belongsTo('objectType').value() as ObjectTypeModel;

      if (selectedObjectType != null) {
        let sentRelAttributes = [...selectedObjectType.sentRelAttributes];
        for (let key in attrs) {
          if (!sentRelAttributes.includes(key)) {
            sentRelAttributes.push(key);
          }
        }

        selectedObjectType.set('sentRelAttributes', sentRelAttributes);
      }

      if (this.args.onRelationshipsAdd) {
        this.args.onRelationshipsAdd();
      } else {
        this.args.onClose();
      }
    } catch {
      this.alerts.error('relationships.add', 'There was an error creating relationships, please try again later');
    }
  });
}
